<template>
	<div>
		<form-input v-model="props.data.name" name="name"
					:rules="{required: true, min: 5, max: 150}"
					:placeholder="$t('placeholder.event.name')"
					:label="$t('label.name')"/>

		<form-club-select name="club_id"
						  :data="organizerSuggestParams"
						  allow-create
						  without-management
						  :color="props.color"
						  :disabled="props.disabled"
						  :placeholder="$t('placeholder.select')"
						  :unselect="$t('label.unknown')"
						  :help="$t('help.event.club_id')"
						  :label="$t('label.organizer')"
						  v-model="props.data.club_id"/>

		<expansion-panels open>
			<expansion-panel :title="$t('label.date_and_time')" :subtitle="$t('help.event.date_and_time')">
				<row>
					<column cols="12" sm="6">
						<form-date-input v-model="props.data.start_date" time
										 name="start_date"
										 :rules="{required: true}"
										 :disabled="props.disabled"
										 :label="$t('label.start_date')"/>
					</column>

					<column cols="12" sm="6">
						<form-date-input v-model="props.data.end_date" time
										 name="end_date"
										 :rules="{required: true, min: props.data.start_date}"
										 :disabled="props.disabled"
										 :label="$t('label.end_date')"/>
					</column>
				</row>
			</expansion-panel>
			<expansion-panel :title="$t('label.address')" :subtitle="$t('help.event.address')">
				<row>
					<column cols="12" md="6">
						<contact-form-inputs :data="props.data" :disabled="props.disabled" disable-help location-name @locationchanged="findEventLatLng"/>

						<alert v-if="invalidAddress" type="warning" text>{{ $t('error.unresolvable_address') }}</alert>
						<alert v-else-if="errorAddress" type="warning" text>{{ $t('error.unresolvable_coordinates') }}</alert>
						<wrapper class="mb-20 md:mb-0" grow>
							<btn small @click.prevent="findEventLatLng(props.data)" :loading="calculatingLatLng" :disabled="props.disabled">
								<icon :icon="icons.location"/>
								<span>{{ $t('action.set_address_on_map') }}</span>
							</btn>
						</wrapper>
					</column>

					<column cols="12" md="6">
						<client-only>
							<map-map id="map-location" ref="locationMap" :center="mapCenter" :enable-large-screen="false" rounded class="min-h-300 h-full" @click="setEventLatLngByMap">

								<map-marker v-if="props.data.lat && props.data.lng" :lat-lng="[props.data.lat, props.data.lng]"
											:draggable="!disabled"
											:color="props.color"
											@moved="latLng => updateEventLatLng(latLng)"/>

								<template #controls>
									<btn-group v-if="props.data.lat && props.data.lng" vertical>
										<tooltip left :text="$t('action.show_event_on_map')">
											<btn small icon @click="mapCenter = [props.data.lat, props.data.lng]" class="w-36 min-w-36 h-36 min-h-36">
												<icon :icon="icons.locate"/>
											</btn>
										</tooltip>
									</btn-group>
								</template>
							</map-map>
						</client-only>
					</column>
				</row>
			</expansion-panel>
			<expansion-panel :title="$t('label.participation_classes')" :subtitle="$t('help.event.participation_classes')">
				<card v-for="(participationClassData, index) in props.data.participation_classes" :key="index" outlined>
					<card-text>
						<participation-class-form-inputs :data="participationClassData" :disabled="props.disabled"/>

						<wrapper v-if="props.data.participation_classes.length > 1">
							<btn @click="removeParticipationClass(index)" small>
								<icon :icon="icons.remove"/>
								<span>{{ $t('action.remove') }}</span>
							</btn>
						</wrapper>
					</card-text>
				</card>
				<wrapper>
					<btn @click="addParticipationClass" small>
						<icon :icon="icons.create"/>
						<span>{{ $t('action.add') }}</span>
					</btn>
				</wrapper>
			</expansion-panel>
		</expansion-panels>

	</div>
</template>

<script setup lang="ts">
import {locationIcon, removeIcon, createIcon} from "@spoferan/nuxt-spoferan/icons";
import {mdiCalendar} from '@mdi/js'

const props = defineProps<{
	// The data on which to store the input data
	data: { [key: string]: any },

	// Whether all inputs shall be disabled
	disabled?: boolean,

	// Whether the inputs are used to edit the resource
	edit?: boolean,

	color?: string
}>();

const {$date} = useNuxtApp();

const icons = {
	create: createIcon,
	remove: removeIcon,
	location: locationIcon,
	locate: mdiCalendar,
};

const organizerSuggestParams = {
	limit: 10
};

const mapCenter = ref(props.data.lat && props.data.lng ? [props.data.lat, props.data.lng] : null);

// States if the latLngs are currently calculated based on the entered address
const calculatingLatLng = ref(false);

// States if the address could not be resolved to valid lat lng coordinates
const invalidAddress = ref(false);

// States if the event's address could not be resolved due to an error
const errorAddress = ref(false);

watch(() => props.data.start_date, (newValue) => {
	if (!newValue) {
		return;
	}

	if (!props.data.end_date) {
		props.data.end_date = $date(newValue).hour(23).minute(59).second(59).format('YYYY-MM-DD HH:mm:ss');
	}
	props.data.participation_classes.forEach((participationClass) => {
		participationClass.start_date = newValue;
	});
});
watch(() => props.data.end_date, (newValue) => {
	if (!newValue) {
		return;
	}

	props.data.participation_classes.forEach((participationClass) => {
		participationClass.end_date = newValue;
	});
});

const {findLatLngByAddress} = useAddressResolver();

/**
 * Tries to find the geolocation of the event's address.
 */
function findEventLatLng(data) {
	calculatingLatLng.value = true;
	findLatLngByAddress(data).then(latLng => {
		if (latLng) {
			updateEventLatLng(latLng);
		} else {
			invalidAddress.value = true;
		}
		calculatingLatLng.value = false;
	}).catch(() => {
		calculatingLatLng.value = false;
		errorAddress.value = true;
	});
}

/**
 * Updated the event's geolocation values with the specified values.
 */
function updateEventLatLng(latLng) {
	invalidAddress.value = false;
	errorAddress.value = false;
	props.data['lat'] = latLng.lat;
	props.data['lng'] = latLng.lng;
	mapCenter.value = props.data.lat && props.data.lng ? [props.data.lat, props.data.lng] : null
}

function setEventLatLngByMap(event) {
	updateEventLatLng(event.latlng);
}

function addParticipationClass() {
	props.data.participation_classes.push({
		start_date: props.data.participation_classes.length ? props.data.participation_classes[props.data.participation_classes.length - 1].start_date : props.data.start_date,
		end_date: props.data.participation_classes.length ? props.data.participation_classes[props.data.participation_classes.length - 1].end_date : props.data.end_date,
	});
}

function removeParticipationClass(index) {
	props.data.participation_classes.splice(index, 1);
}

</script>